import { UndefinedAble } from 'data/base';
import { AuthType } from '../AuthConfig/AuthType';
import { Module } from '../Module';
import {
  AuthConfig,
  ModuleConfig,
  ModuleConfigDescription,
  ModuleIntegrationConfigDescription,
  PlatformData,
} from '../ModuleConfig';
import { ModuleVersion } from '../ModuleVersion';
import { AndroidModuleConfigDataFields } from './AndroidModuleConfigData';
import { IosModuleConfigDataFields } from './IosModuleConfigData';
import { ReactNativeModuleConfigDataFields } from './ReactNativeModuleConfigData';
import { WebModuleConfigDataFields } from './WebModuleConfigData';

export enum PlatformCode {
  AndroidNative = 'android',
  IOSNative = 'ios',
  AndroidReactNative = 'react_android',
  IOSReactNative = 'react_ios',
  Web = 'web',
}

export function getPlatformDataFields(
  platformCode: PlatformCode,
  filters: string[],
  filterInclusive: boolean
): string[] {
  let fields: string[] = [];
  switch (platformCode) {
    case PlatformCode.AndroidNative:
      fields = AndroidModuleConfigDataFields;
      break;
    case PlatformCode.IOSNative:
      fields = IosModuleConfigDataFields;
      break;
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      fields = ReactNativeModuleConfigDataFields;
      break;
    case PlatformCode.Web:
      fields = WebModuleConfigDataFields;
      break;
    default:
      fields = [];
      break;
  }

  return fields.filter(
    (f: string) =>
      (filterInclusive && filters.includes(f)) ||
      (!filterInclusive && !filters.includes(f))
  );
}

export function isValidConfig(
  platformCode: PlatformCode,
  config?: ModuleConfigDescription<any, any>
): boolean {
  return (
    isValidPlatformConfig(platformCode, config) && isValidAuthConfig(config)
  );
}

export function isValidPlatformConfig(
  platformCode: PlatformCode,
  config?: ModuleConfigDescription<any, any>
): boolean {
  switch (platformCode) {
    case PlatformCode.AndroidNative:
      return (
        config?.data?.['@type'] ===
        'type.googleapis.com/terra.v1.AndroidModuleConfigData'
      );
    case PlatformCode.IOSNative:
      return (
        config?.data?.['@type'] ===
        'type.googleapis.com/terra.v1.IosModuleConfigData'
      );
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      return (
        config?.data?.['@type'] ===
        'type.googleapis.com/terra.v1.ReactModuleConfigData'
      );
    case PlatformCode.Web:
      return (
        config?.data?.['@type'] ===
        'type.googleapis.com/terra.v1.WebModuleConfigData'
      );
    default:
      return false;
  }
}

export function isValidAuthConfig(
  config?: ModuleConfigDescription<any, any>
): boolean {
  switch (config?.authType) {
    case AuthType.None:
      return config?.authConfig === null;
    case AuthType.IdTokenExchange:
      return (
        config?.authConfig?.['@type'] ===
        'type.googleapis.com/terra.v1.IdTokenExchangeAuthConfig'
      );
    default:
      return false;
  }
}

export function getBlankModuleConfig(
  platformCode: PlatformCode
): ModuleIntegrationConfigDescription<any, any> {
  switch (platformCode) {
    case PlatformCode.AndroidNative:
      return {
        data: AndroidModuleConfigDataFields.reduce(
          (acc, cur) => ({ ...acc, [cur]: '' }),
          { '@type': 'type.googleapis.com/terra.v1.AndroidModuleConfigData' }
        ),
        extra: {},
        authType: AuthType.None,
        authConfig: undefined,
        paymentConfigId: '',
        terraClientId: undefined,
        displayInfo: {
          name: '',
          iconUrl: '',
          order: '0',
        },
      };
    case PlatformCode.IOSNative:
      return {
        data: IosModuleConfigDataFields.reduce(
          (acc, cur) => ({ ...acc, [cur]: '' }),
          { '@type': 'type.googleapis.com/terra.v1.IosModuleConfigData' }
        ),
        extra: {},
        authType: AuthType.None,
        authConfig: undefined,
        paymentConfigId: '',
        terraClientId: undefined,
        displayInfo: {
          name: '',
          iconUrl: '',
          order: '0',
        },
      };
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      return {
        data: ReactNativeModuleConfigDataFields.reduce(
          (acc, cur) => ({ ...acc, [cur]: '' }),
          {
            '@type': 'type.googleapis.com/terra.v1.ReactModuleConfigData',
            componentName: 'RNMain',
            enableDeveloperSupport: false,
          }
        ),
        extra: {},
        authType: AuthType.None,
        authConfig: undefined,
        paymentConfigId: '',
        terraClientId: undefined,
        displayInfo: {
          name: '',
          iconUrl: '',
          order: '0',
        },
      };
    case PlatformCode.Web:
      return {
        data: WebModuleConfigDataFields.reduce(
          (acc, cur) => ({ ...acc, [cur]: '' }),
          { '@type': 'type.googleapis.com/terra.v1.WebModuleConfigData' }
        ),
        extra: {},
        authType: AuthType.None,
        authConfig: undefined,
        paymentConfigId: '',
        terraClientId: undefined,
        displayInfo: {
          name: '',
          iconUrl: '',
          order: '0',
        },
      };
    default:
      throw Error(
        `getBlankModuleManifest: Unsupported platformCode ${platformCode}`
      );
  }
}

export function getModuleIntegrationConfigFromModuleConfig(
  config: ModuleConfig<any, any>
): ModuleIntegrationConfigDescription<any, any> {
  return {
    ...config,
    paymentConfigId: '',
  };
}

export interface ModuleVersionTemplate<
  T extends PlatformData,
  A extends AuthConfig
> {
  code: string;
  config: ModuleConfigDescription<T, A>;
  configId: UndefinedAble<string>;
}

export function createModuleVersionTemplate<
  T extends PlatformData,
  A extends AuthConfig
>(
  fromModule: Module,
  fromVersion: UndefinedAble<ModuleVersion>,
  fromConfig: UndefinedAble<ModuleConfig<T, A>>
): ModuleVersionTemplate<T, A> {
  const blank = createBlankModuleVersionTemplate<T, A>(
    fromModule.platform.code as PlatformCode
  );
  if (fromVersion && fromConfig) {
    return {
      code: fromVersion.code,
      config: fromConfig,
      configId: fromConfig.id,
    };
  } else if (fromVersion) {
    return {
      code: fromVersion.code,
      config: fromConfig || blank.config,
      configId: fromConfig?.id,
    };
  } else {
    return blank;
  }
}

export function createBlankModuleVersionTemplate<
  T extends PlatformData,
  A extends AuthConfig
>(platformCode: PlatformCode): ModuleVersionTemplate<T, A> {
  switch (platformCode) {
    case PlatformCode.AndroidNative:
    case PlatformCode.IOSNative:
      return {
        code: '',
        config: {
          data: ({
            '@type': getDataTypeField(platformCode),
            mainClass: '',
            initClass: '',
          } as unknown) as T,
          extra: {},
          authType: AuthType.None,
          authConfig: undefined,
          terraClientId: undefined,
          displayInfo: {
            name: '',
            iconUrl: '',
            order: '0',
          },
        },
        configId: undefined,
      };
    case PlatformCode.Web:
      return {
        code: '',
        config: {
          data: ({
            '@type': getDataTypeField(platformCode),
            url: '',
          } as unknown) as T,
          extra: {},
          authType: AuthType.None,
          authConfig: undefined,
          terraClientId: undefined,
          displayInfo: {
            name: '',
            iconUrl: '',
            order: '0',
          },
        },
        configId: undefined,
      };
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      return {
        code: '',
        config: {
          data: ({
            '@type': getDataTypeField(platformCode),
            componentName: 'RNMain',
            enableDeveloperSupport: false,
          } as unknown) as T,
          extra: {},
          authType: AuthType.None,
          authConfig: undefined,
          terraClientId: undefined,
          displayInfo: {
            name: '',
            iconUrl: '',
            order: '0',
          },
        },
        configId: undefined,
      };
    default:
      throw Error(`Invalid platform code ${platformCode}`);
  }
}

export function getDataTypeField(platformCode: PlatformCode): string {
  switch (platformCode) {
    case PlatformCode.AndroidNative:
      return 'type.googleapis.com/terra.v1.AndroidModuleConfigData';
    case PlatformCode.IOSNative:
      return 'type.googleapis.com/terra.v1.IosModuleConfigData';
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      return 'type.googleapis.com/terra.v1.ReactModuleConfigData';
    case PlatformCode.Web:
      return 'type.googleapis.com/terra.v1.WebModuleConfigData';
    default:
      throw Error(`Invalid platform code ${platformCode}`);
  }
}

function getPlatformCode(config: any): PlatformCode {
  if (
    config['@type'] === 'type.googleapis.com/terra.v1.AndroidModuleConfigData'
  ) {
    return PlatformCode.AndroidNative;
  } else if (
    config['@type'] === 'type.googleapis.com/terra.v1.IosModuleConfigData'
  ) {
    return PlatformCode.IOSNative;
  } else if (
    config['@type'] === 'type.googleapis.com/terra.v1.ReactModuleConfigData'
  ) {
    return PlatformCode.AndroidReactNative; // PlatformCode.IOSReactNative
  } else if (
    config['@type'] === 'type.googleapis.com/terra.v1.WebModuleConfigData'
  ) {
    return PlatformCode.Web;
  } else {
    throw Error(`Invalid config data`);
  }
}

export function normalizeConfig<T extends PlatformData>(config: any): T {
  const platformCode = getPlatformCode(config);

  switch (platformCode) {
    case PlatformCode.AndroidNative:
      return config as T;
    case PlatformCode.IOSNative:
      return config as T;
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      return {
        ...config,
        enableDeveloperSupport:
          config.enableDeveloperSupport === 'true' ? true : false,
      } as T;
    case PlatformCode.Web:
      return config as T;
    default:
      return config as T;
  }
}

export function convertConfigForEditting<T extends PlatformData>(
  config: any
): T {
  const platformCode = getPlatformCode(config);

  switch (platformCode) {
    case PlatformCode.AndroidNative:
      return config as T;
    case PlatformCode.IOSNative:
      return config as T;
    case PlatformCode.AndroidReactNative:
    case PlatformCode.IOSReactNative:
      return {
        ...config,
        enableDeveloperSupport: config.enableDeveloperSupport
          ? 'true'
          : 'false',
      } as T;
    case PlatformCode.Web:
      return config as T;
    default:
      return config as T;
  }
}
